import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  Divider,
  Grid,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  TextField,
  Button,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination
} from '@mui/material';
import { useSelector } from 'react-redux';
import RefreshIcon from '@mui/icons-material/Refresh';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmailIcon from '@mui/icons-material/Email';
import InfoIcon from '@mui/icons-material/Info';

import { Configuration } from '../../../assets/data/config';
import { apiServices } from '../../../services/apiServices';

const SendInvitation = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const [apiCount, setApiCount] = useState(0);

  // New state variables for email invites
  const [emailInput, setEmailInput] = useState('');
  const [sendingInvites, setSendingInvites] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState(false);
  const [inviteError, setInviteError] = useState(null);

  // New state variables for invited users
  const [invitedUsers, setInvitedUsers] = useState([]);
  const [loadingInvitedUsers, setLoadingInvitedUsers] = useState(false);
  const [invitedUsersError, setInvitedUsersError] = useState(null);

  // Table pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchTerm, setSearchTerm] = useState('');

  // Helper function to get email count and validation status
  const getEmailCountInfo = () => {
    if (!emailInput.trim()) return { count: 0, isValid: false, isOverLimit: false };

    // Improved email parsing: handle multiple consecutive separators and filter out empty strings
    const emailList = emailInput
      .split(/[,;\n]+/) // Use + to handle multiple consecutive separators
      .map(email => email.trim())
      .filter(email => email.length > 0 && email.includes('@')); // Basic email validation

    const count = emailList.length;
    const isOverLimit = count > 7;
    const isValid = count > 0 && !isOverLimit;

    // Debug logging
    console.log('Email parsing debug:', {
      originalInput: emailInput,
      splitEmails: emailInput.split(/[,;\n]+/),
      trimmedEmails: emailInput.split(/[,;\n]+/).map(email => email.trim()),
      filteredEmails: emailList,
      count,
      isOverLimit,
      isValid
    });

    return { count, isValid, isOverLimit };
  };

  const { user } = useSelector((state) => state.auth);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const fetchInvitedUsers = async () => {
    if (!user?.peacekeeper_id) {
      setInvitedUsersError('Peacekeeper ID not found');
      return;
    }

    setLoadingInvitedUsers(true);
    setInvitedUsersError(null);

    try {
      const response = await apiServices.getAPI(`invitations/${user.peacekeeper_id}`);
      if (response && response.status >= 200 && response.status < 300) {
        const data = response.data;
        console.log('Invitations API Response:', data);

        // Handle the API response format
        let invitationsArray = [];

        if (data && data.invitations && Array.isArray(data.invitations)) {
          // API returns { message, count, peacekeeperId, invitations: [...] }
          invitationsArray = data.invitations;
        } else if (Array.isArray(data)) {
          // Fallback: direct array response
          invitationsArray = data;
        } else if (data && Array.isArray(data)) {
          // Another possible format
          invitationsArray = data;
        } else {
          console.warn('Unexpected invitations API response format:', data);
          invitationsArray = [];
        }

        setInvitedUsers(invitationsArray);
      } else {
        throw new Error(`HTTP ${response?.status}: ${response?.statusText}`);
      }
    } catch (err) {
      console.error('Error fetching invited users:', err);
      setInvitedUsersError(err.message || 'Failed to fetch invited users');
    } finally {
      setLoadingInvitedUsers(false);
    }
  };

  useEffect(() => {
    fetchInvitedUsers();
  }, [user?.peacekeeper_id]);

  const handleRefresh = () => {
    fetchInvitedUsers();
  };

  const handleSendEmailInvites = async () => {
    if (!emailInput.trim()) {
      setInviteError('Please enter at least one email address');
      return;
    }

    if (!user?.peacekeeper_id) {
      setInviteError('Peacekeeper ID not found');
      return;
    }

    // Process and validate email addresses
    const emailList = emailInput
      .split(/[,;\n]+/) // Use + to handle multiple consecutive separators
      .map(email => email.trim())
      .filter(email => email.length > 0 && email.includes('@')); // Basic email validation

    if (emailList.length === 0) {
      setInviteError('Please enter at least one valid email address');
      return;
    }

    // Check maximum email limit (7 emails)
    if (emailList.length > 7) {
      setInviteError(`Maximum 7 email addresses allowed. You entered ${emailList.length} emails.`);
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = emailList.filter(email => !emailRegex.test(email));

    if (invalidEmails.length > 0) {
      setInviteError(`Invalid email format: ${invalidEmails.slice(0, 3).join(', ')}${invalidEmails.length > 3 ? '...' : ''}`);
      return;
    }

    setSendingInvites(true);
    setInviteError(null);

    try {
      // Format emails as semicolon-separated string for the API
      const formattedEmails = emailList.join(';');
      const payload = {
        emailIds: formattedEmails,
        peacekeeperId: user.peacekeeper_id,
        referrerName: user.username || user.fullName || 'Unknown User'
      };
      const response = await apiServices.postAPI('send-email-invites', payload);

      if (response && response.status >= 200 && response.status < 300) {
        const data = response.data;
        console.log('Email invites sent successfully:', data);
        setInviteSuccess(true);
        setEmailInput('');
        // Refresh both lists to show any new registrations and invitations
        setTimeout(() => {
          fetchInvitedUsers();
        }, 1000);
      } else {
        const errorMessage = response?.data?.message || `HTTP ${response?.status}: ${response?.statusText}`;
        throw new Error(errorMessage);
      }
    } catch (err) {
      console.error('Error sending email invites:', err);
      setInviteError(err.message || 'Failed to send email invites');
    } finally {
      setSendingInvites(false);
    }
  };

  const handleEmailInputChange = (event) => {
    setEmailInput(event.target.value);
    // Clear any previous errors when user starts typing
    if (inviteError) {
      setInviteError(null);
    }
  };

  const handleCloseSnackbar = () => {
    setInviteSuccess(false);
    setInviteError(null);
  };

  const handleResendInvitation = async (email) => {
    if (!email || !user?.peacekeeper_id) {
      setInviteError('Invalid email or Peacekeeper ID not found');
      return;
    }

    setSendingInvites(true);
    setInviteError(null);

    try {
      const payload = {
        emailIds: email,
        peacekeeperId: user.peacekeeper_id,
        referrerName: user.username || user.fullName || 'Unknown User'
      };
      const response = await apiServices.postAPI('send-email-invites', payload);

      if (response && response.status >= 200 && response.status < 300) {
        const data = response.data;
        console.log('Resend invitation successful:', data);
        setInviteSuccess(true);
        // Refresh both lists to show updated status
        setTimeout(() => {
          fetchInvitedUsers();
        }, 1000);
      } else {
        const errorMessage = response?.data?.message || `HTTP ${response?.status}: ${response?.statusText}`;
        throw new Error(errorMessage);
      }
    } catch (err) {
      console.error('Error resending invitation:', err);
      setInviteError(err.message || 'Failed to resend invitation');
    } finally {
      setSendingInvites(false);
    }
  };

  // Generate referral link based on usertype and temp_peacekeeper_id
  const generateReferralLink = () => {
    if (!user) return '';

    const domain = Configuration.frontendDomain;
    const peacekeeperId = user.peacekeeper_id || 'IN-0000000-PK-IAPM';

    // Priority 1: For Entity usertype with categoryname, include category in URL
    // Format: domain/register?Name-Category-PeacekeeperID
    if (user.usertype === 'Entity' && user.categoryname) {
      const cleanName = (user.name || user.username || 'User')
        .replace(/[^a-zA-Z0-9\s]/g, '')
        .replace(/\s+/g, '-');
      const cleanCategory = user.categoryname
        .replace(/[^a-zA-Z0-9\s]/g, '')
        .replace(/\s+/g, '-');
      return `${domain}/register?${cleanName}-${cleanCategory}-${peacekeeperId}`;
    }

    // Priority 2: For users referred by entities (have temp_peacekeeper_id)
    // Format: domain/register?{temp_peacekeeper_id}
    if (user.temp_peacekeeper_id) {
      return `${domain}/register?${user.temp_peacekeeper_id}`;
    }

    // Priority 3: For regular users (no entity, no temp ID)
    // Format: domain/register?referencenumber={peacekeeper_id}
    return `${Configuration.frontendDomain}/register?${user?.username?.replace(/\s+/g, '-') || 'User'}-${user?.peacekeeper_id || 'IN-0000000-PK-IAPM'}`
    // `${domain}/register?referencenumber=${peacekeeperId}`;
  };

  const handleCopyReferralLink = async () => {
    const referralLink = generateReferralLink();

    try {
      await navigator.clipboard.writeText(referralLink);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = referralLink;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const handleReferralLinkClick = () => {
    const referralLink = generateReferralLink();
    
    // For mobile devices, force open in system browser
    if (window.cordova) {
      // Try to open in system browser using _system target
      try {
        // Use _system target to force open in device's default browser
        window.cordova.InAppBrowser.open(referralLink, '_system', 'location=yes');
      } catch (error) {
        console.log('Failed to open in system browser:', error);
        // Fallback: try window.open with _system
        try {
          window.open(referralLink, '_system');
        } catch (fallbackError) {
          console.log('Fallback also failed:', fallbackError);
          // Last resort: regular window.open
          window.open(referralLink, '_blank');
        }
      }
    } else {
      // For web browsers, use standard window.open
      window.open(referralLink, '_blank');
    }
  };

  const handleWhatsAppInvite = (phone, name) => {
    const message = `Hi! I'm inviting you to join the Peacekeeper Movement. Visit ${Configuration.frontendDomain} to learn more and register.`;
    const whatsappUrl = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleEmailInvite = (email, name) => {
    const subject = 'Invitation to Join the Peacekeeper Movement';
    const body = `Hi ${name},\n\nI'm inviting you to join the Peacekeeper Movement, a global initiative promoting peace and harmony.\n\nVisit ${Configuration.frontendDomain} to learn more and register.\n\nBest regards,\n${user?.name || user?.username || 'A Peacekeeper'}`;

    const mailtoUrl = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoUrl;
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'N/A';
    }
  };

  const getInvitationStatusColor = (status) => {
    if (!status) return 'default';

    const statusLower = status.toLowerCase();
    if (statusLower === 'accepted' || statusLower === 'completed' || statusLower === 'success') {
      return 'success';
    } else if (statusLower === 'pending' || statusLower === 'sent') {
      return 'warning';
    } else if (statusLower === 'rejected' || statusLower === 'failed') {
      return 'error';
    } else if (statusLower === 'expired') {
      return 'default';
    } else {
      return 'info';
    }
  };

  // Table pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Reset pagination when invited users change
  useEffect(() => {
    setPage(0);
    setSearchTerm('');
  }, [invitedUsers.length]);

  // Filter invited users based on search term
  const filteredInvitedUsers = invitedUsers.filter(user => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (user.name && user.name.toLowerCase().includes(searchLower)) ||
      (user.email && user.email.toLowerCase().includes(searchLower)) ||
      (user.emailid && user.emailid.toLowerCase().includes(searchLower)) ||
      (user.phone && user.phone.includes(searchTerm)) ||
      (user.status && user.status.toLowerCase().includes(searchLower)) ||
      (user.message && user.message.toLowerCase().includes(searchLower))
    );
  });

  if ((loading || loadingInvitedUsers) && invitedUsers.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }} mt={0}>
      {/* Header Section */}
      <Box
        elevation={1}
        sx={{
          // p: 3, 
          mb: 2,
          // background: 'linear-gradient(135deg, #214F7D 0%, #3A8139 100%)',
          // color: 'white'
        }}

      >
        <Grid container alignItems="center" justifyContent="space-between">
          <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Manage and invite people to join the <strong>I Am Peacekeeper Movement</strong>
            </Typography>
          </Box>
          <Grid item>
            <Tooltip title="Refresh List">
              <IconButton
                onClick={handleRefresh}
                disabled={loading}
                sx={{ color: '#252525', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </Box>

      {/* Referral Link Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography fontWeight={600} gutterBottom sx={{ mb: 2 }}>
            Your Referral Link
          </Typography>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap'
          }}>
            <Box sx={{
              flex: 1,
              minWidth: 200,
              p: 2,
              bgcolor: 'grey.50',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'grey.300',
              fontFamily: 'monospace',
              fontSize: '14px',
              wordBreak: 'break-all',
              cursor: 'pointer',
              '&:hover': {
                bgcolor: 'grey.100'
              }
            }}
            onClick={handleReferralLinkClick}
            >
              <Typography
                sx={{
                  color: '#1976d2',
                  textDecoration: 'none',
                  cursor: 'pointer',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                {generateReferralLink()}
              </Typography>
            </Box>
            <Tooltip title={copySuccess ? "Copied!" : "Copy Link"}>
              <IconButton
                onClick={handleCopyReferralLink}
                sx={{
                  bgcolor: copySuccess ? 'success.main' : 'primary.main',
                  color: 'white',
                  '&:hover': {
                    bgcolor: copySuccess ? 'success.dark' : 'primary.dark'
                  }
                }}
              >
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Share this link with family, friends and colleagues to invite them to join the Movement.
          </Typography>
        </CardContent>
      </Card>

      {/* Email Invites Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography fontWeight={600} gutterBottom sx={{ mb: 2 }}>
            Send Email Invites
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter multiple email addresses, separated by commas, semicolons, or line breaks. Each person will
            receive an official invitation to join the <strong>I Am Peacekeeper Movement</strong>.
          </Typography>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Email addresses"
                variant="outlined"
                value={emailInput}
                onChange={handleEmailInputChange}
                error={!!inviteError}
                helperText={
                  inviteError ||
                  "Separate multiple emails with commas, semicolons, or new lines. Maximum 7 emails allowed."
                }
                placeholder="example@example.com, another@example.com&#10;third@example.com; fourth@example.com"
                multiline
                rows={4}
                sx={{ mb: 2 }}
              />
              <Typography variant="caption" color="text.secondary">
                💡 Tip: You can copy-paste a list of emails from Excel or other sources.
              </Typography>
              {emailInput.trim() && (() => {
                const { count } = getEmailCountInfo();
                if (count > 7) {
                  return (
                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                      ⚠️ Maximum limit exceeded! Please reduce to 7 or fewer emails.
                    </Typography>
                  );
                } else if (count === 7) {
                  return (
                    <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 1 }}>
                      ✅ Perfect! You've reached the maximum of 7 emails (7/7)
                    </Typography>
                  );
                } else if (count >= 5) {
                  return (
                    <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>
                      ℹ️ {count}/7 emails - approaching limit
                    </Typography>
                  );
                }
                return null;
              })()}
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                variant="contained"
                fullWidth
                onClick={handleSendEmailInvites}
                disabled={sendingInvites || !getEmailCountInfo().isValid}
                startIcon={sendingInvites ? <CircularProgress size={24} color="#fff" /> : <EmailOutlinedIcon />}
                sx={{
                  background: 'linear-gradient(to right, #214F7D, #3A8139)',
                  color: 'white',
                  borderRadius: "50px"
                }}
              >
                {sendingInvites ?
                  `Sending to ${getEmailCountInfo().count} email(s)...`
                  : 'Send Invites'
                }
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                {(() => {
                  const { count, isOverLimit } = getEmailCountInfo();
                  if (count === 0) {
                    return 'No valid emails detected';
                  } else if (isOverLimit) {
                    return (
                      <span style={{ color: '#d32f2f' }}>
                        {count}/7 emails detected (Over limit!)
                      </span>
                    );
                  } else {
                    return `${count}/7 valid email(s) detected`;
                  }
                })()}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Invited Users Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography fontWeight={600} gutterBottom sx={{ mb: 2 }}>
            Invited Users
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>View the list of people you have invited.</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>

            <Tooltip title="Track the status of users you've invited to join the Peacekeeper Movement. This shows who has been invited, their current status, and when they were invited.">
              <IconButton size="small" sx={{ color: 'text.secondary' }}>
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          {/* <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Track the status of users you've invited to join the Peacekeeper Movement
          </Typography> */}

          {loadingInvitedUsers ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          ) : invitedUsersError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {invitedUsersError}
            </Alert>
          ) : invitedUsers.length > 0 ? (
            <>
              {/* Search and Filter Section */}
              <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                  size="small"
                  placeholder="Search by name, email, or status"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={{ minWidth: 300, flex: 1 }}
                  InputProps={{
                    startAdornment: (
                      <Box sx={{ mr: 1, color: 'text.secondary' }}>
                        <EmailIcon fontSize="small" />
                      </Box>
                    ),
                  }}
                />
                {searchTerm && (
                  <Button
                    size="small"
                    onClick={() => setSearchTerm('')}
                    sx={{ minWidth: 'auto' }}
                  >
                    Clear
                  </Button>
                )}
              </Box>

              {/* Summary */}
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Showing {filteredInvitedUsers.length} of {invitedUsers.length} invited users
                  {searchTerm && ` (filtered by "${searchTerm}")`}
                </Typography>
              </Box>

              {/* Cards Layout */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {filteredInvitedUsers
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((invitedUser, index) => (
                    <Card key={invitedUser.id || invitedUser.email || index} sx={{ borderRadius: isMobile ? 2 : 1 }}>
                      <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                        <Box sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          width: '100%'
                        }}>
                          {/* Avatar and Email Section - Centered */}
                          <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 1,
                            width: '100%'
                          }}>
                            <Avatar sx={{
                              bgcolor: 'secondary.main',
                              width: isMobile ? 56 : 64,
                              height: isMobile ? 56 : 64,
                              fontSize: isMobile ? '18px' : '20px',
                              mb: 1
                            }}>
                              {getInitials(invitedUser.name || invitedUser.emailid)}
                            </Avatar>

                            <Typography sx={{
                              fontWeight: 'bold',
                              fontSize: isMobile ? '16px' : '18px',
                              textAlign: 'center',
                              wordBreak: 'break-word'
                            }}>
                              {invitedUser.name || invitedUser.emailid || 'Unknown User'}
                            </Typography>

                            {invitedUser.email && (
                              <Typography variant="body2" color="text.secondary" sx={{
                                fontSize: isMobile ? '13px' : '14px',
                                textAlign: 'center'
                              }}>
                                {invitedUser.email}
                              </Typography>
                            )}

                            <Chip
                              label={invitedUser.status || 'pending'}
                              size="small"
                              color={getInvitationStatusColor(invitedUser.status)}
                              sx={{
                                fontWeight: '300',
                                fontSize: isMobile ? '11px' : '12px',
                                height: isMobile ? 20 : 24
                              }}
                            />
                          </Box>

                          {/* Additional Details */}
                          <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                            width: '100%'
                          }}>
                            {invitedUser.created_at && (
                              <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 1,
                                p: 1,
                                bgcolor: 'grey.50',
                                borderRadius: 1
                              }}>
                                <Typography variant="body2" color="text.secondary" sx={{
                                  fontSize: isMobile ? '12px' : '14px'
                                }}>
                                  <strong>Invited:</strong> {formatDate(invitedUser.created_at)}
                                </Typography>
                              </Box>
                            )}

                            {invitedUser.phone && (
                              <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 1,
                                p: 1.5,
                                bgcolor: 'grey.50',
                                borderRadius: 1
                              }}>
                                <Typography variant="body2" color="text.secondary" sx={{
                                  fontSize: isMobile ? '12px' : '14px'
                                }}>
                                  <strong>Phone:</strong> {invitedUser.phone}
                                </Typography>
                              </Box>
                            )}
                          </Box>

                          {/* Actions Section */}
                          <Box sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            gap: 2,
                            justifyContent: 'center',
                            mt: 1,
                            width: '100%'
                          }}>
                            <Tooltip title="Resend Invitation">
                              {isMobile ? (
                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={() => handleResendInvitation(invitedUser.email || invitedUser.emailid)}
                                  sx={{
                                    background: 'linear-gradient(to right, #214F7D, #3A8139)',
                                    color: 'white',
                                    borderRadius: "50px",
                                    px: 3,
                                    py: 1
                                  }}
                                >
                                  Invite
                                </Button>
                              ) : (
                                <IconButton
                                  size="small"
                                  onClick={() => handleResendInvitation(invitedUser.email || invitedUser.emailid)}
                                  sx={{
                                    color: 'primary.main',
                                    bgcolor: 'primary.50',
                                    '&:hover': {
                                      backgroundColor: 'primary.100'
                                    }
                                  }}
                                >
                                  <EmailOutlinedIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Tooltip>

                            {invitedUser.phone && (
                              <Tooltip title="Send WhatsApp Invite">
                                <IconButton
                                  size="small"
                                  onClick={() => handleWhatsAppInvite(invitedUser.phone, invitedUser.name)}
                                  sx={{
                                    color: 'success.main',
                                    bgcolor: 'success.50',
                                    '&:hover': {
                                      backgroundColor: 'success.100'
                                    }
                                  }}
                                >
                                  <WhatsAppIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
              </Box>

              {/* Pagination */}
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                <TablePagination
                  component="div"
                  count={filteredInvitedUsers.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  labelRowsPerPage={isMobile ? "Per page:" : "Rows per page:"}
                  labelDisplayedRows={({ from, to, count }) =>
                    isMobile ? `${from}-${to} of ${count}` : `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
                  }
                  sx={{
                    '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                      fontSize: isMobile ? '12px' : '14px'
                    },
                    '.MuiTablePagination-select': {
                      fontSize: isMobile ? '12px' : '14px'
                    }
                  }}
                />
              </Box>
            </>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <EmailOutlinedIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography fontWeight={600} color="text.secondary" gutterBottom>
                No Invitations Yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                You haven't sent any email invitations yet. Use the form above to invite people to join!
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Snackbar for Email Invite Messages */}
      <Snackbar
        open={inviteSuccess || !!inviteError}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {inviteSuccess ? (
          <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
            Email invites sent successfully!
          </Alert>
        ) : inviteError ? (
          <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
            {inviteError}
          </Alert>
        ) : null}
      </Snackbar>
    </Box>
  );
};

export default SendInvitation;
